import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Coins, Receipt, Download, Search, Loader2 } from "lucide-react";
import { providerLabel, fmtMoney } from "@/lib/payments";

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const SUB_STATUS = {
  active: { label: "Actif", bg: "#D1FAE5", color: "#065F46" },
  cancelled_active_until: { label: "Annulé (actif jusqu'à échéance)", bg: "#FEF3C7", color: "#92400E" },
  past_due: { label: "Paiement en échec", bg: "#FEE2E2", color: "#991B1B" },
  expired: { label: "Expiré", bg: "#F3F4F6", color: "#374151" },
  revoked: { label: "Retiré", bg: "#F3F4F6", color: "#374151" },
  refunded: { label: "Remboursé", bg: "#F3F4F6", color: "#374151" },
};
const KIND_LABEL = {
  verified_initial: "Yalola Vérifié — 1er paiement", verified_renewal: "Yalola Vérifié — renouvellement", verified_refund: "Yalola Vérifié — remboursement",
  adcredit_purchase: "Crédits pub — recharge", adcredit_refund: "Crédits pub — remboursement",
  donation_fee: "Soutien — frais facturés au donateur", donation_fee_refund: "Soutien — frais remboursés",
  donation_commission: "Soutien — commission", donation_commission_refund: "Soutien — commission remboursée",
  ad_campaign_stripe: "Publicité (ancien paiement Stripe)", other: "Autre",
};

// Search a member by name / e-mail / phone (GET /admin/users?q=) and pick one.
const UserPicker = ({ value, onPick, testId = "user-picker" }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setRows([]); return undefined; }
    const id = setTimeout(() => api.get(`/admin/users?q=${encodeURIComponent(s)}&limit=8`).then(({ data }) => setRows(data.users || [])).catch(() => setRows([])), 300);
    return () => clearTimeout(id);
  }, [q]);
  if (value) {
    return (
      <div className="flex items-center justify-between rounded-xl px-3 py-2 text-sm" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`${testId}-selected`}>
        <span><b>{value.name}</b> <span style={{ color: "var(--yalola-text-muted)" }}>{value.email || value.phone || value.id}</span></span>
        <button type="button" className="underline text-xs" onClick={() => onPick(null)} data-testid={`${testId}-clear`}>changer</button>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-3" style={{ color: "var(--yalola-text-muted)" }} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail ou téléphone…" className="pl-8" data-testid={`${testId}-input`} />
      </div>
      {rows.length > 0 && (
        <div className="rounded-xl border divide-y max-h-48 overflow-auto" style={{ borderColor: "var(--yalola-border)" }}>
          {rows.map((u) => (
            <button key={u.id} type="button" onClick={() => onPick(u)} className="w-full text-left px-3 py-2 text-sm hover:bg-black/5" data-testid={`${testId}-option-${u.id}`}>
              <b>{u.name}</b> <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{u.email || u.phone} · {u.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Yalola Vérifié — all providers (stripe / apple / google / admin) + free grant / revoke
// ---------------------------------------------------------------------------
export const VerifiedSubscriptionsAdmin = ({ canManage = true }) => {
  const { user } = useApp();
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [provider, setProvider] = useState("");
  const [grantOpen, setGrantOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [duration, setDuration] = useState("year");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [revoke, setRevoke] = useState(null);

  const load = () => api.get(`/admin/verified-subscriptions${provider ? `?provider=${provider}` : ""}`).then(({ data: d }) => setData(d)).catch(() => setData({ subscriptions: [], counts: {} }));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [provider]);

  const grant = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.post("/admin/verified-subscriptions/grant", { user_id: target.id, duration, reason });
      toast.success(`Badge attribué à ${target.name} ✓`);
      setGrantOpen(false); setTarget(null); setReason(""); load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  const doRevoke = async () => {
    if (!revoke) return;
    setBusy(true);
    try {
      await api.post("/admin/verified-subscriptions/revoke", { user_id: revoke.user_id, reason });
      toast.success("Badge retiré");
      setRevoke(null); setReason(""); load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };

  const counts = data?.counts || {};
  const totalLive = Object.values(counts).reduce((a, s) => a + (s.active || 0) + (s.cancelled_active_until || 0) + (s.past_due || 0), 0);
  return (
    <div className="space-y-3" data-testid="admin-verified-subs">
      <div className="yalola-card p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-bold font-display"><ShieldCheck size={18} style={{ color: "var(--yalola-accent)" }} /> Yalola Vérifié — {totalLive} actif{totalLive > 1 ? "s" : ""}</div>
        <div className="flex gap-1 flex-wrap text-xs" data-testid="verified-subs-counts">
          {["stripe", "apple", "google", "admin"].map((p) => (
            <button key={p} type="button" onClick={() => setProvider(provider === p ? "" : p)} className={`chip ${provider === p ? "chip-primary" : ""}`} data-testid={`verified-subs-filter-${p}`}>
              {providerLabel(p)} · {Object.values(counts[p] || {}).reduce((a, b) => a + b, 0)}
            </button>
          ))}
        </div>
        {canManage && isSuper && (
          <Button size="sm" className="rounded-full pill-primary ml-auto" onClick={() => setGrantOpen(true)} data-testid="verified-grant-open">Attribuer gratuitement</Button>
        )}
      </div>
      {data === null ? <AdminSkeleton /> : (data.subscriptions || []).length === 0 ? (
        <div className="yalola-card p-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="verified-subs-empty">Aucun abonnement.</div>
      ) : (
        <div className="yalola-card overflow-x-auto" data-testid="verified-subs-table">
          <Table>
            <TableHeader><TableRow><TableHead>Membre</TableHead><TableHead>Fournisseur</TableHead><TableHead>Statut</TableHead><TableHead>Prix</TableHead><TableHead>Depuis le</TableHead><TableHead>Échéance</TableHead><TableHead>Env.</TableHead>{canManage && isSuper && <TableHead />}</TableRow></TableHeader>
            <TableBody>
              {data.subscriptions.map((s) => {
                const st = SUB_STATUS[s.status] || { label: s.status, bg: "#F3F4F6", color: "#374151" };
                return (
                  <TableRow key={s.id || `${s.user_id}-${s.provider_ref}`} data-testid={`verified-sub-row-${s.user_id}`}>
                    <TableCell className="text-xs"><b>{s.user?.name || s.user_id}</b><div style={{ color: "var(--yalola-text-muted)" }}>{s.user?.email}</div></TableCell>
                    <TableCell className="text-xs">{providerLabel(s.provider)}{s.granted_by_user ? <div style={{ color: "var(--yalola-text-muted)" }}>par {s.granted_by_user.name}</div> : null}</TableCell>
                    <TableCell><span className="chip text-[10px]" style={{ background: st.bg, color: st.color }}>{st.label}</span></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{s.provider === "admin" ? "—" : fmtMoney(s.price ?? 0, s.currency || "EUR")}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap font-medium" data-testid={`verified-sub-since-${s.user_id}`}>{fmtDate(s.started_at || s.created_at)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{s.current_period_end ? fmtDate(s.current_period_end) : (s.provider === "admin" ? "sans expiration" : "—")}</TableCell>
                    <TableCell className="text-xs">{s.environment || "—"}</TableCell>
                    {canManage && isSuper && (
                      <TableCell>{s.live && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setRevoke(s)} data-testid={`verified-revoke-${s.user_id}`}>Retirer</Button>}</TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={grantOpen} onOpenChange={(v) => { if (!v) { setGrantOpen(false); setTarget(null); } }}>
        <DialogContent className="max-w-md" data-testid="verified-grant-dialog">
          <DialogHeader><DialogTitle>Attribuer Yalola Vérifié</DialogTitle><DialogDescription>Badge offert par Yalola (fournisseur « admin », aucun paiement). Remplace un éventuel abonnement payant en cours.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <UserPicker value={target} onPick={setTarget} testId="verified-grant-user" />
            <div>
              <Label>Durée</Label>
              <div className="flex gap-2 mt-1">
                {[["year", "1 an"], ["lifetime", "À vie"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setDuration(k)} className={`chip ${duration === k ? "chip-primary" : ""}`} data-testid={`verified-grant-duration-${k}`}>{l}</button>
                ))}
              </div>
            </div>
            <div><Label>Motif (interne)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. partenaire, ambassadeur…" data-testid="verified-grant-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setGrantOpen(false)}>Annuler</Button>
            <Button className="rounded-full pill-primary" onClick={grant} disabled={!target || busy} data-testid="verified-grant-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : "Attribuer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revoke} onOpenChange={(v) => !v && setRevoke(null)}>
        <DialogContent className="max-w-md" data-testid="verified-revoke-dialog">
          <DialogHeader><DialogTitle>Retirer le badge de {revoke?.user?.name}</DialogTitle><DialogDescription>Le badge disparaît immédiatement. Un abonnement payant (Stripe / App Store / Google Play) n'est pas remboursé ici : gère le remboursement dans la console du fournisseur.</DialogDescription></DialogHeader>
          <div><Label>Motif (interne)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="verified-revoke-reason" /></div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setRevoke(null)}>Annuler</Button>
            <Button className="rounded-full" variant="destructive" onClick={doRevoke} disabled={busy} data-testid="verified-revoke-submit">Retirer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Ad credits — sold by provider vs spent, outstanding balances, manual adjustments
// ---------------------------------------------------------------------------
const AC_KIND = { purchase: "Recharge", spend: "Dépense", refund: "Remboursement", revoke: "Recharge annulée", admin_adjust: "Ajustement admin" };

export const AdCreditsAdmin = ({ canManage = true }) => {
  const { user, config } = useApp();
  const isSuper = user?.role === "super_admin";
  const cur = config?.payments?.currency || config?.settings?.default_currency || "EUR";
  const [data, setData] = useState(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/ad-credits").then(({ data: d }) => setData(d)).catch(() => setData({ transactions: [] }));
  useEffect(() => { load(); }, []);

  const adjust = async () => {
    const amt = Number(amount);
    if (!target || !amt) return;
    setBusy(true);
    try {
      const { data: d } = await api.post("/admin/ad-credits/adjust", { user_id: target.id, amount: amt, reason });
      toast.success(`Solde de ${target.name} : ${fmtMoney(d.balance, cur)}`);
      setAdjOpen(false); setTarget(null); setAmount(""); setReason(""); load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };

  if (data === null) return <AdminSkeleton />;
  const sold = data.sold_by_provider || {};
  return (
    <div className="space-y-3" data-testid="admin-ad-credits">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Crédits vendus", data.sold_total, "var(--yalola-primary)", "ac-sold"],
          ["Crédits dépensés", data.spent, "#8A5A12", "ac-spent"],
          ["Soldes en circulation", data.outstanding, "#1E40AF", "ac-outstanding"],
          ["Remboursés / ajustés", (data.refunded || 0) + (data.adjusted || 0), "#6B7280", "ac-refunded"],
        ].map(([l, v, c, tid]) => (
          <div key={tid} className="yalola-card p-4" data-testid={tid}>
            <div className="text-[11px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{l}</div>
            <div className="text-xl font-extrabold font-display mt-1" style={{ color: c }}>{fmtMoney(v || 0, cur)}</div>
          </div>
        ))}
      </div>
      <div className="yalola-card p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-bold font-display"><Coins size={18} style={{ color: "var(--yalola-primary)" }} /> Ventes par fournisseur</div>
        {["stripe", "apple", "google"].map((p) => (
          <span key={p} className="chip text-xs" data-testid={`ac-sold-${p}`}>{providerLabel(p)} : {fmtMoney(sold[p]?.total || 0, cur)} ({sold[p]?.count || 0})</span>
        ))}
        <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{data.holders || 0} membre{(data.holders || 0) > 1 ? "s" : ""} avec un solde</span>
        {canManage && isSuper && <Button size="sm" className="rounded-full pill-primary ml-auto" onClick={() => setAdjOpen(true)} data-testid="ac-adjust-open">Ajuster un solde</Button>}
      </div>
      {(data.transactions || []).length === 0 ? (
        <div className="yalola-card p-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="ac-empty">Aucun mouvement de crédits pour l'instant.</div>
      ) : (
        <div className="yalola-card overflow-x-auto" data-testid="ac-table">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Membre</TableHead><TableHead>Type</TableHead><TableHead>Fournisseur</TableHead><TableHead>Libellé</TableHead><TableHead className="text-right">Montant</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.transactions.map((r) => (
                <TableRow key={r.id} data-testid={`ac-row-${r.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                  <TableCell className="text-xs"><b>{r.user?.name || r.user_id}</b></TableCell>
                  <TableCell className="text-xs">{AC_KIND[r.kind] || r.kind}</TableCell>
                  <TableCell className="text-xs">{r.provider && r.provider !== "wallet" ? providerLabel(r.provider) : "—"}</TableCell>
                  <TableCell className="text-xs max-w-[220px] truncate" title={r.label}>{r.label || "—"}</TableCell>
                  <TableCell className="text-xs text-right font-semibold whitespace-nowrap" style={{ color: Number(r.amount) >= 0 ? "var(--yalola-secondary)" : "var(--yalola-text)" }}>{Number(r.amount) >= 0 ? "+" : "−"}{fmtMoney(Math.abs(Number(r.amount) || 0), r.currency || cur)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={adjOpen} onOpenChange={(v) => { if (!v) { setAdjOpen(false); setTarget(null); } }}>
        <DialogContent className="max-w-md" data-testid="ac-adjust-dialog">
          <DialogHeader><DialogTitle>Ajuster des crédits publicitaires</DialogTitle><DialogDescription>Montant positif = crédits offerts, négatif = retrait. Le membre est notifié.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <UserPicker value={target} onPick={setTarget} testId="ac-adjust-user" />
            <div><Label>Montant ({cur})</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Ex. 20 ou -5" data-testid="ac-adjust-amount" /></div>
            <div><Label>Motif</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. geste commercial" data-testid="ac-adjust-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAdjOpen(false)}>Annuler</Button>
            <Button className="rounded-full pill-primary" onClick={adjust} disabled={!target || !Number(amount) || busy} data-testid="ac-adjust-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : "Appliquer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Revenue ledger — gross / provider fees / net Yalola by kind and by provider (+ CSV)
// ---------------------------------------------------------------------------
export const RevenueLedgerAdmin = () => {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [dl, setDl] = useState(false);
  const months = React.useMemo(() => {
    const out = [{ key: "", label: "Tout l'historique" }];
    const now = new Date();
    for (let i = 0; i < 12; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) });
    }
    return out;
  }, []);
  useEffect(() => {
    setData(null);
    api.get(`/admin/finance/revenue${month ? `?month=${month}` : ""}`).then(({ data: d }) => setData(d)).catch(() => setData({ by_currency: {} }));
  }, [month]);
  const download = async () => {
    setDl(true);
    try {
      const res = await api.get(`/admin/finance/revenue/transactions.csv${month ? `?month=${month}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `yalola-revenus-${month || "tout"}.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
    finally { setDl(false); }
  };
  const byCur = data?.by_currency || {};
  const currencies = Object.keys(byCur);
  return (
    <div className="space-y-3" data-testid="admin-revenue-ledger">
      <div className="yalola-card p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-bold font-display"><Receipt size={18} style={{ color: "var(--yalola-primary)" }} /> Journal des revenus (multi-fournisseur)</div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-md border px-2 text-sm capitalize" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="ledger-month">
          {months.map((m) => <option key={m.key} value={m.key} className="capitalize">{m.label}</option>)}
        </select>
        <Button size="sm" variant="outline" className="rounded-full ml-auto" onClick={download} disabled={dl} data-testid="ledger-csv"><Download size={14} className="mr-1" /> CSV</Button>
      </div>
      <p className="text-xs px-1" style={{ color: "var(--yalola-text-muted)" }}>Brut = encaissé · Frais = commission du fournisseur (Stripe réel ; App Store / Google Play au taux réel RevenueCat, sinon taux administré sur le prix hors TVA) · Taxes = TVA collectée par le store · Net = brut − frais − taxes = ce qui revient à Yalola. Les soutiens n'apparaissent que via leurs frais (0 % de commission, 100 % versé au bénéficiaire).</p>
      {data === null ? <AdminSkeleton /> : currencies.length === 0 ? (
        <div className="yalola-card p-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="ledger-empty">Aucune transaction sur la période.</div>
      ) : currencies.map((c) => {
        const x = byCur[c];
        return (
          <div key={c} className="space-y-3" data-testid={`ledger-currency-${c}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["Brut encaissé", x.gross, "var(--yalola-primary)", "gross"], ["Frais fournisseurs", x.provider_fees, "#B91C1C", "fees"], ["Taxes", x.tax, "#6B7280", "tax"], ["Net Yalola", x.net, "#065F46", "net"]].map(([l, v, col, k]) => (
                <div key={k} className="yalola-card p-4" data-testid={`ledger-${c}-${k}`}>
                  <div className="text-[11px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{l} ({c})</div>
                  <div className="text-xl font-extrabold font-display mt-1" style={{ color: col }}>{fmtMoney(v || 0, c)}</div>
                </div>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {[["Par type", x.by_kind, (k) => KIND_LABEL[k] || k, "kind"], ["Par fournisseur", x.by_provider, providerLabel, "provider"]].map(([title, grp, lab, key]) => (
                <div key={key} className="yalola-card overflow-x-auto" data-testid={`ledger-${c}-by-${key}`}>
                  <div className="px-4 pt-3 font-semibold text-sm">{title}</div>
                  <Table>
                    <TableHeader><TableRow><TableHead>{title.replace("Par ", "")}</TableHead><TableHead className="text-right">Nb</TableHead><TableHead className="text-right">Brut</TableHead><TableHead className="text-right">Frais</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {Object.entries(grp || {}).map(([k, b]) => (
                        <TableRow key={k} data-testid={`ledger-${c}-${key}-${k}`}>
                          <TableCell className="text-xs font-semibold">{lab(k)}</TableCell>
                          <TableCell className="text-xs text-right">{b.count}</TableCell>
                          <TableCell className="text-xs text-right">{fmtMoney(b.gross, c)}</TableCell>
                          <TableCell className="text-xs text-right">{fmtMoney(b.provider_fees, c)}</TableCell>
                          <TableCell className="text-xs text-right font-semibold">{fmtMoney(b.net, c)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
